import { BillEvent } from './billEvent';
import { PaymentTransaction } from './paymentTransaction';
import { Address } from './address';

export interface ManualBillItem {
  id: string;
  description: string;
  amount: number; // Can be negative for discounts
  createdBy: string; // User ID who created the item
  createdByName?: string; // Display name
  createdAt: Date;
  updatedAt: Date;
}

// Helper function to generate manual item IDs
export const generateManualItemId = (): string => {
  return `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export interface BillData {
  apartmentId: string;
  apartmentName: string;
  travellers: {
    name: string;
    accommodationCost: number;
    skiPassCost: number;
    transportationCost: number;
    membershipFee: number;
    childDiscount: number;
    total: number;
  }[];
  totalAccommodation: number;
  totalSkiPass: number;
  totalTransportation: number;
  totalMembership: number;
  totalChildDiscount: number;
  grandTotal: number;
  billStatus: string;
  isPaid: boolean;
}

export enum BillStatus {
  DRAFT = 'draft',
  GENERATED = 'generated', 
  SENT = 'sent',
  PARTIALLY_PAID = 'partially_paid',
  FULLY_PAID = 'fully_paid'
}

export interface Bill {
  id: string;
  tripId: string;
  apartmentId: string;
  apartmentName: string;
  
  // Bill recipient - the person responsible for the bill
  billRecipientId: string; // ID of the traveller who is the bill recipient
  
  // Bill content snapshot (immutable once generated)
  travellers: {
    id: string;
    name: string;
    address: Address;
    accommodationCost: number;
    skiPassCost: number;
    transportationCost: number;
    membershipFee: number;
    childDiscount: number;
    total: number;
  }[];
  totalAccommodation: number;
  totalSkiPass: number;
  totalTransportation: number;
  totalMembership: number;
  totalChildDiscount: number;
  grandTotal: number;
  
  // Bill metadata
  status: BillStatus;
  isPaid: boolean;
  paidAmount: number;
  generatedAt: Date;
  generatedBy: string; // User ID who generated the bill
  sentAt?: Date;
  paidAt?: Date;
  
  // Events timeline - chronological list of all bill events
  events: BillEvent[];
  
  // Payment transactions - detailed tracking of individual payments
  paymentTransactions: PaymentTransaction[];
  
  // Manual invoice items - for special adjustments
  manualItems: ManualBillItem[];
  
  // Audit trail
  createdAt: Date;
  updatedAt: Date;
}

// Compatibility alias - to be removed after migration
export type PersistentBill = Bill;
